import React, { useState, useEffect, useMemo } from 'react';
import styled, { css } from 'styled-components';
import tw from 'twin.macro';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQuery } from '@apollo/client';
import { toast } from 'react-toastify';
import { UPDATE_CAR } from '../../../services/carService/queries';
import { GET_PROTECTION_PLANS } from '../../../services/protectionPlanService/queries';
import { GET_TRIP_FEES } from '../../../services/tripFeeServices/queries';
import { useUpdateEditCarStoreHook } from '../../../hooks/useUpdateEditCarStoreHook';
import { useHostCapabilities } from '../../../hooks/useHostCapabilities';
import { useSnapshot } from 'valtio';
import { carStore } from '../../../../store/carStore';
import { authStore } from '../../../../store/authStore';
import {
  ITripFee,
  ProtectionPlanEnum,
  TripFeeUniqNameEnums,
} from '../../../../__generated__/graphql';
import { useBrandLocale } from '../../../hooks/useBrandLocale';
import { defaultDailyDistance, defaultMonthlyDistance } from '../../../utils/units';

const Container = styled.div`
  ${tw`max-w-lg mx-auto p-6 flex flex-col items-center justify-center`}
`;

const Title = styled.h1`
  ${tw`text-2xl font-bold mb-4`}
`;

const FormGrid = styled.div`
  ${tw`grid grid-cols-1 gap-4 w-full`}
`;

const FormGroup = styled.div`
  ${tw`mb-4 w-full`}
`;

const Label = styled.label`
  ${tw`block text-text-secondary font-medium mb-2`}
`;

const InputContainer = styled.div`
  ${tw`w-full mx-auto mb-4`}
`;

const StyledInput = styled.input`
  ${tw`w-full p-2 border border-border-default rounded-md`}
  box-sizing: border-box;
`;

const NextButton = styled.button<{ isActivated: boolean }>`
  ${tw`w-full text-white py-3 rounded-lg font-semibold transition-colors shadow-xs`}
  ${({ isActivated }) =>
    isActivated
      ? css`
          ${tw`bg-black`}
          &:hover {
            ${tw`bg-black-hover`}
          }
        `
      : tw`bg-bg-secondary text-text-tertiary cursor-not-allowed`}
`;

const BackButton = styled.button`
  ${tw`w-full py-3 rounded-md border-2 border-border-default bg-white text-text-primary font-semibold transition-colors mb-3`}
  &:hover {
    ${tw`bg-bg-secondary`}
  }
`;

const ButtonContainer = styled.div`
  ${tw`flex flex-col justify-center w-full mt-6`}
`;

const Description = styled.p`
  ${tw`text-text-secondary mb-6 text-center max-w-md`}
`;

const EligibilityWarning = styled.div`
  ${tw`flex flex-col gap-2 p-4 rounded-lg mb-6 max-w-md w-full border`}
  background-color: #fef2f2;
  border-color: #fecaca;
  color: #991b1b;
  font-size: 0.875rem;
`;

const AdvisoryWarning = styled.div`
  ${tw`flex flex-col gap-2 p-4 rounded-lg mb-6 max-w-md w-full border`}
  background-color: #fffbeb;
  border-color: #fde68a;
  color: #92400e;
  font-size: 0.875rem;
`;

const WarningHeaderRow = styled.div`
  ${tw`flex flex-wrap items-center justify-between gap-3`}
`;

const WarningHeading = styled.p`
  ${tw`m-0 font-semibold leading-snug flex-1 min-w-0`}
`;

const WarningCopy = styled.p`
  ${tw`m-0 leading-relaxed`}
`;

const MonthlyFieldsHint = styled.p`
  ${tw`m-0 text-sm text-text-secondary leading-relaxed`}
`;

const SetupNotice = styled.div`
  ${tw`flex flex-col gap-3 p-4 rounded-lg mb-6 max-w-md w-full border`}
  background-color: #fffbeb;
  border-color: #fde68a;
  color: #92400e;
  font-size: 0.875rem;
`;

const StepCard = styled.button`
  ${tw`w-full text-left border border-border-default rounded-lg p-4 bg-white cursor-pointer transition-colors hover:bg-bg-secondary flex flex-col gap-2`}
  color: #111827;
`;

const StepBadge = styled.span`
  ${tw`text-xs font-semibold uppercase tracking-wide text-text-secondary`}
`;

const StepTitle = styled.span`
  ${tw`text-base font-semibold text-text-primary`}
`;

const StepCopy = styled.span`
  ${tw`text-sm text-text-secondary leading-relaxed`}
`;

const EditTripPrefsButton = styled.button`
  ${tw`shrink-0 font-medium px-3 py-1.5 rounded-md border transition-colors`}
  border-color: #fecaca;
  background-color: #ffffff;
  color: #991b1b;
  font-size: inherit;
  &:hover {
    background-color: #fee2e2;
  }
`;

type MonthlyPlanRow = {
  plan: ProtectionPlanEnum;
  isActive?: boolean | null;
};

type TripFeeRow = {
  uniqName: TripFeeUniqNameEnums;
  name: string;
};

const MONTHLY_PROTECTION_PLANS = [
  ProtectionPlanEnum.MonthlyMinimum,
  ProtectionPlanEnum.MonthlyStandard,
  ProtectionPlanEnum.MonthlyPro,
  ProtectionPlanEnum.MonthlyPremium,
];

const MONTHLY_TRIP_FEE_ROWS: TripFeeRow[] = [
  {
    uniqName: TripFeeUniqNameEnums.MonthlyTripfee,
    name: 'Monthly Trip Fee',
  },
  {
    uniqName: TripFeeUniqNameEnums.MonthlyYoungsterfee18,
    name: 'Monthly <21 Youngster Fee',
  },
  {
    uniqName: TripFeeUniqNameEnums.MonthlyYoungsterfee21,
    name: 'Monthly 21-25 Youngster Fee',
  },
];

function findMonthlyTripFee(fees: ITripFee[], row: TripFeeRow): ITripFee | undefined {
  return fees.find(f => f.uniqName === row.uniqName || f.name === row.name);
}

function isMonthlyTripFeeRowConfigured(fee: ITripFee | undefined): boolean {
  return !!fee?.id && fee.isActive === true;
}

export default function AddMonthlyPricePage() {
  const navigate = useNavigate();
  // Spec 014b: monthly pricing is the brand's when it sets it; go on to photos.
  const { can: canHost, loading: capabilitiesLoading } = useHostCapabilities();
  useEffect(() => {
    if (!capabilitiesLoading && !canHost('carMonthlyPricing')) {
      navigate('/cars/host/add-photos', { replace: true });
    }
  }, [capabilitiesLoading, canHost, navigate]);
  const { editingCar, successfullUpdatedEditingCarId } = useUpdateEditCarStoreHook();
  const carStoreSnapshot = useSnapshot(carStore);
  const currentEditingCar = editingCar || carStoreSnapshot.editingCar;
  const userId = authStore.user?.id;

  const [monthlyRentalIsOn, setMonthlyRentalIsOn] = useState(false);
  const [monthlyPrice, setMonthlyPrice] = useState('');
  const [monthlyPrice2Months, setMonthlyPrice2Months] = useState('');
  const [monthlyPrice3Months, setMonthlyPrice3Months] = useState('');
  const [monthlyDistanceLimit, setMonthlyDistanceLimit] = useState('');
  // Spec 028: this site's unit.
  const { distanceUnit } = useBrandLocale();
  const [monthlyUnlimitedMileage, setMonthlyUnlimitedMileage] = useState(false);

  const [updateCar, { loading }] = useMutation(UPDATE_CAR);

  const { data: protectionData, loading: protectionLoading } = useQuery(GET_PROTECTION_PLANS, {
    fetchPolicy: 'cache-and-network',
  });

  const { data: tripFeesData, loading: tripFeesLoading } = useQuery(GET_TRIP_FEES, {
    variables: { userId: userId || '' },
    skip: !userId,
    fetchPolicy: 'cache-and-network',
  });

  useEffect(() => {
    if (currentEditingCar) {
      const ec: any = currentEditingCar as any;
      setMonthlyRentalIsOn(!!ec.monthlyRentalIsOn);
      setMonthlyPrice(ec.monthlyPrice != null ? String(ec.monthlyPrice) : '');
      setMonthlyPrice2Months(ec.monthlyPrice2Months != null ? String(ec.monthlyPrice2Months) : '');
      setMonthlyPrice3Months(ec.monthlyPrice3Months != null ? String(ec.monthlyPrice3Months) : '');
      setMonthlyDistanceLimit(
        ec.distanceLimit?.monthlyDistanceLimit != null
          ? String(ec.distanceLimit.monthlyDistanceLimit)
          : '',
      );
      setMonthlyUnlimitedMileage(!!ec.distanceLimit?.monthlyUnlimitedMileage);
    }
  }, [currentEditingCar]);

  const handlePriceChange =
    (setter: React.Dispatch<React.SetStateAction<string>>) =>
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const value = event.target.value.replace(/[^0-9.]/g, '');
      setter(value);
    };

  const ecDistanceLimit = (currentEditingCar as any)?.distanceLimit ?? {};
  const editingCarId = currentEditingCar?.id ?? '';

  const maxDays = (currentEditingCar as any)?.maximumRentalDays;
  const monthlyBlocked = maxDays != null && maxDays > 0 && maxDays < 30;

  const hasMonthlyProtectionSetup = useMemo(() => {
    const plans = (protectionData as { protectionPlans?: MonthlyPlanRow[] })?.protectionPlans;
    return MONTHLY_PROTECTION_PLANS.some(plan =>
      plans?.some(p => p.plan === plan && p.isActive === true),
    );
  }, [protectionData]);

  const hasMonthlyTripFeesSetup = useMemo(() => {
    const fees = (tripFeesData?.tripFees ?? []) as ITripFee[];
    return MONTHLY_TRIP_FEE_ROWS.every(row =>
      isMonthlyTripFeeRowConfigured(findMonthlyTripFee(fees, row)),
    );
  }, [tripFeesData]);

  const monthlySetupLoading = protectionLoading || (!!userId && tripFeesLoading);
  const monthlySetupReady = hasMonthlyProtectionSetup && hasMonthlyTripFeesSetup;
  const monthlySetupRequired = monthlyRentalIsOn && !monthlySetupReady;
  const monthlySetupBlocking = monthlyRentalIsOn && (monthlySetupLoading || !monthlySetupReady);

  const onContinue = async () => {
    try {
      if (!successfullUpdatedEditingCarId || !currentEditingCar?.id) {
        if (!currentEditingCar?.id) navigate('/cars/host/add');
        return;
      }

      if (monthlyRentalIsOn && (monthlySetupLoading || !monthlySetupReady)) {
        toast.error(
          'Set a monthly protection plan and monthly trip fees before enabling monthly rental.',
        );
        return;
      }

      const monthlyDistanceParsed = monthlyDistanceLimit
        ? parseFloat(monthlyDistanceLimit)
        : (ecDistanceLimit.monthlyDistanceLimit ?? defaultMonthlyDistance(distanceUnit));

      await updateCar({
        variables: {
          id: currentEditingCar.id,
          updateCarInput: {
            monthlyRentalIsOn,
            monthlyPrice: monthlyPrice ? parseFloat(monthlyPrice) : undefined,
            monthlyPrice2Months: monthlyPrice2Months ? parseFloat(monthlyPrice2Months) : undefined,
            monthlyPrice3Months: monthlyPrice3Months ? parseFloat(monthlyPrice3Months) : undefined,
            distanceLimit: {
              dailyDistanceLimit:
                ecDistanceLimit.dailyDistanceLimit ?? defaultDailyDistance(distanceUnit),
              unlimitedMileage: !!ecDistanceLimit.unlimitedMileage,
              monthlyDistanceLimit: Number.isFinite(monthlyDistanceParsed)
                ? monthlyDistanceParsed
                : defaultMonthlyDistance(distanceUnit),
              monthlyUnlimitedMileage,
            },
          },
        },
      });

      navigate('/cars/host/add-photos');
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Unable to save monthly pricing';
      toast.error(message);
    }
  };

  if (!successfullUpdatedEditingCarId) {
    return (
      <Container>
        <Title>Loading...</Title>
      </Container>
    );
  }

  if (!currentEditingCar?.id) {
    return (
      <Container>
        <Title>Car not found</Title>
        <Description>Please go back and start the listing process again.</Description>
        <ButtonContainer>
          <NextButton isActivated onClick={() => navigate('/cars/host/add')}>
            Go Back
          </NextButton>
        </ButtonContainer>
      </Container>
    );
  }

  return (
    <Container>
      <Title>Set Monthly Car Pricing</Title>
      <Description>
        Optional: enable monthly rentals and set 30 / 60 / 90 day prices and monthly mileage. You
        already set daily rates on the previous step.
      </Description>

      <FormGrid>
        <FormGroup>
          <Label>Enable monthly rental</Label>
          <InputContainer>
            <input
              type="checkbox"
              checked={monthlyRentalIsOn}
              onChange={e => setMonthlyRentalIsOn(e.target.checked)}
            />
          </InputContainer>
        </FormGroup>
      </FormGrid>

      {!monthlyRentalIsOn && (
        <AdvisoryWarning>
          <WarningHeading>Monthly rental is off</WarningHeading>
          <WarningCopy>
            Monthly bookings will not use monthly pricing or monthly mileage until you turn this
            switch on.
          </WarningCopy>
        </AdvisoryWarning>
      )}

      {monthlyBlocked && (
        <EligibilityWarning>
          <WarningHeaderRow>
            <WarningHeading>Trip duration blocks monthly rental</WarningHeading>
            <EditTripPrefsButton
              type="button"
              onClick={() =>
                navigate(
                  `/cars/host/view/${encodeURIComponent(editingCarId)}?section=tripPreferences`,
                )
              }
            >
              Edit trip preferences
            </EditTripPrefsButton>
          </WarningHeaderRow>
          <WarningCopy>
            The maximum trip duration is set to <strong>{maxDays} days</strong>. Update it to 30
            days or more in Trip Preferences, and keep the monthly rental switch on, to allow
            monthly bookings.
          </WarningCopy>
        </EligibilityWarning>
      )}

      {monthlySetupRequired && (
        <SetupNotice>
          <WarningHeading>Monthly setup required</WarningHeading>
          <WarningCopy>
            Monthly rental needs a saved monthly protection plan and monthly trip fees before this
            listing can be enabled for monthly bookings.
          </WarningCopy>
          {!monthlySetupLoading && !hasMonthlyProtectionSetup && (
            <StepCard
              type="button"
              onClick={() => navigate('/cars/host/protection-plans?tab=monthly')}
            >
              <StepBadge>Step 1</StepBadge>
              <StepTitle>Set monthly protection plan</StepTitle>
              <StepCopy>Activate and save at least one monthly protection plan.</StepCopy>
            </StepCard>
          )}
          {!monthlySetupLoading && !hasMonthlyTripFeesSetup && (
            <StepCard
              type="button"
              onClick={() => navigate('/cars/host/miscellaneous-fees?tab=monthly')}
            >
              <StepBadge>Step 2</StepBadge>
              <StepTitle>Set monthly trip fees</StepTitle>
              <StepCopy>Save monthly trip fee and both monthly youngster fees as active.</StepCopy>
            </StepCard>
          )}
        </SetupNotice>
      )}

      {monthlyRentalIsOn && (
        <FormGrid>
          <FormGroup>
            <Label>30-Day Price ($)</Label>
            <InputContainer>
              <StyledInput
                type="text"
                value={monthlyPrice}
                placeholder="e.g. 1200"
                onChange={handlePriceChange(setMonthlyPrice)}
                data-testid="car-monthly-price-1-input"
              />
            </InputContainer>
          </FormGroup>
          <FormGroup>
            <Label>60-Day Price ($)</Label>
            <InputContainer>
              <StyledInput
                type="text"
                value={monthlyPrice2Months}
                placeholder="e.g. 2000"
                onChange={handlePriceChange(setMonthlyPrice2Months)}
                data-testid="car-monthly-price-2-input"
              />
            </InputContainer>
          </FormGroup>
          <FormGroup>
            <Label>90-Day Price ($)</Label>
            <InputContainer>
              <StyledInput
                type="text"
                value={monthlyPrice3Months}
                placeholder="e.g. 2700"
                onChange={handlePriceChange(setMonthlyPrice3Months)}
                data-testid="car-monthly-price-3-input"
              />
            </InputContainer>
          </FormGroup>
          <FormGroup>
            <Label>
              {distanceUnit === 'km' ? 'Monthly distance limit (km)' : 'Monthly mileage limit'}
            </Label>
            <InputContainer>
              <StyledInput
                type="text"
                value={monthlyDistanceLimit}
                placeholder="e.g. 1000"
                onChange={handlePriceChange(setMonthlyDistanceLimit)}
              />
            </InputContainer>
          </FormGroup>
          <FormGroup>
            <Label>Unlimited monthly distance</Label>
            <InputContainer>
              <input
                type="checkbox"
                checked={monthlyUnlimitedMileage}
                onChange={e => setMonthlyUnlimitedMileage(e.target.checked)}
              />
            </InputContainer>
          </FormGroup>
          <MonthlyFieldsHint>
            Monthly pricing is only used for bookings of 30 days or more when the monthly switch is
            on.
          </MonthlyFieldsHint>
        </FormGrid>
      )}

      <ButtonContainer>
        <BackButton type="button" onClick={() => navigate('/cars/host/add-price')}>
          Back to daily pricing
        </BackButton>
        <NextButton
          type="button"
          onClick={onContinue}
          isActivated={!loading && !monthlySetupBlocking}
          disabled={loading || monthlySetupBlocking}
          data-testid="car-monthly-price-next"
        >
          {loading ? 'Saving…' : 'Next'}
        </NextButton>
      </ButtonContainer>
    </Container>
  );
}
